import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { PROFILE_ROUTES, ROUTES } from 'configs';
import { selectCurrentUserSubscription } from 'store/selectors/subscriptions';
import { UserSubscription } from 'types/subscription';
import { getBillingPortalUrl } from 'utils/subscriptionBilling';
import {
  getBillingAttentionReason,
  BillingAttentionReason,
} from 'utils/subscriptionState';

import {
  BannerActions,
  BannerAccent,
  BannerBar,
  BannerBody,
  BannerCloseButton,
  BannerCopy,
  BannerCta,
  BannerInner,
  BannerRoot,
  BannerSecondary,
  BannerTitle,
} from './styled';

const subscriptionPath = `${ROUTES.PROFILE}/${PROFILE_ROUTES.SUBSCRIPTION}`;

const DISMISS_STORAGE_PREFIX = 'gumb:billing-attention:dismissed:';

const copyKey = (
  reason: BillingAttentionReason,
  part: 'title' | 'body' | 'cta',
) => `subscriptions.billingAttention.${reason}.${part}`;

function getDismissStorageKey(
  subscription: UserSubscription,
  reason: BillingAttentionReason,
): string {
  const subId = subscription.subscriptionId ?? subscription.id;
  const stripeStatus = subscription.stripeSubscriptionStatus ?? 'unknown';
  return `${DISMISS_STORAGE_PREFIX}${subId}:${stripeStatus}:${reason}`;
}

function isDismissedInSession(
  subscription: UserSubscription | null | undefined,
  reason: BillingAttentionReason | null,
): boolean {
  if (!subscription || !reason) return false;
  try {
    return (
      sessionStorage.getItem(getDismissStorageKey(subscription, reason)) === '1'
    );
  } catch {
    return false;
  }
}

const BillingAttentionBanner: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const subscription = useSelector(selectCurrentUserSubscription);

  const reason = useMemo(
    () => getBillingAttentionReason(subscription),
    [subscription],
  );

  const [dismissed, setDismissed] = useState(() =>
    isDismissedInSession(subscription, reason),
  );

  useEffect(() => {
    setDismissed(isDismissedInSession(subscription, reason));
  }, [
    subscription,
    reason,
    subscription?.subscriptionId,
    subscription?.stripeSubscriptionStatus,
    subscription?.updatedAt,
  ]);

  const portalUrl = useMemo(
    () => getBillingPortalUrl(subscription ?? undefined),
    [subscription],
  );

  const openExternal = useCallback((url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const handlePrimary = useCallback(() => {
    if (portalUrl) {
      openExternal(portalUrl);
      return;
    }
    navigate(subscriptionPath);
  }, [portalUrl, openExternal, navigate]);

  const handleSecondary = useCallback(() => {
    navigate(subscriptionPath);
  }, [navigate]);

  const handleDismiss = useCallback(() => {
    if (!subscription || !reason) return;
    try {
      sessionStorage.setItem(getDismissStorageKey(subscription, reason), '1');
    } catch {
      // ignore storage errors
    }
    setDismissed(true);
  }, [subscription, reason]);

  if (!reason || dismissed) return null;

  const primaryLabel = portalUrl
    ? t(copyKey(reason, 'cta'))
    : t('subscriptions.billingAttention.viewSubscription');

  return (
    <BannerRoot role="alert" data-billing-attention={reason}>
      <BannerInner>
        <BannerAccent>
          <BannerBar aria-hidden />
          <BannerCopy>
            <BannerTitle>{t(copyKey(reason, 'title'))}</BannerTitle>
            <BannerBody>{t(copyKey(reason, 'body'))}</BannerBody>
          </BannerCopy>
        </BannerAccent>
        <BannerActions>
          <BannerCta type="button" onClick={handlePrimary}>
            {primaryLabel}
          </BannerCta>
          {portalUrl ? (
            <BannerSecondary type="button" onClick={handleSecondary}>
              {t('subscriptions.billingAttention.viewSubscription')}
            </BannerSecondary>
          ) : null}
        </BannerActions>
        <BannerCloseButton
          type="button"
          onClick={handleDismiss}
          aria-label={t('subscriptions.billingAttention.dismiss')}
        >
          ×
        </BannerCloseButton>
      </BannerInner>
    </BannerRoot>
  );
};

export default BillingAttentionBanner;
